import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Sliders, Lock } from 'lucide-react';

const SCOPES = [
  { value: 'global', label: 'TFS Global (default)' },
  { value: 'trade', label: 'By Trade' },
  { value: 'customer', label: 'By Customer' },
  { value: 'customer_trade', label: 'By Customer + Trade' },
];

// Internal-only. Configurable, versioned, effective-dated rates. Nothing is hard-coded;
// unset fields resolve to "Rate required". Never exposed to clients or field personnel.
const RateSettingsManager = () => {
  const { token, user } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const internal = ['owner', 'admin', 'manager', 'tfs_operations'].includes(user?.role);

  const [fields, setFields] = useState([]);
  const [labels, setLabels] = useState({});
  const [current, setCurrent] = useState([]);
  const [trades, setTrades] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [scope, setScope] = useState('global');
  const [customerId, setCustomerId] = useState('');
  const [tradeId, setTradeId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [note, setNote] = useState('');
  const [rates, setRates] = useState({});

  const [resCust, setResCust] = useState('');
  const [resTrade, setResTrade] = useState('');
  const [resolved, setResolved] = useState(null);

  const load = useCallback(async () => {
    try {
      const [f, c, t, cu] = await Promise.all([
        axios.get(`${API}/rate-settings/fields`, authHeaders),
        axios.get(`${API}/rate-settings`, authHeaders),
        axios.get(`${API}/trades`, authHeaders),
        axios.get(`${API}/customers/names`, authHeaders),
      ]);
      setFields(f.data.fields || []); setLabels(f.data.labels || {});
      setCurrent(c.data || []); setTrades(t.data || []); setCustomers(cu.data || []);
    } catch (e) { /* internal guard may 403 for non-internal */ }
  }, [token]);

  useEffect(() => { if (internal) load(); }, [load, internal]);

  const save = async () => {
    if (scope.includes('customer') && !customerId) { toast.error('Select a customer'); return; }
    if (scope.includes('trade') && !tradeId) { toast.error('Select a trade'); return; }
    const cleanRates = {};
    Object.entries(rates).forEach(([k, v]) => { if (v !== '' && v !== undefined && v !== null) cleanRates[k] = parseFloat(v); });
    if (Object.keys(cleanRates).length === 0) { toast.error('Enter at least one rate value'); return; }
    try {
      await axios.post(`${API}/rate-settings`, {
        scope_type: scope,
        customer_id: scope.includes('customer') ? customerId : null,
        trade_id: scope.includes('trade') ? tradeId : null,
        rates: cleanRates, effective_date: effectiveDate || null, note: note || null,
      }, authHeaders);
      toast.success('Rate settings saved (new version)');
      setRates({}); setNote(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  const doResolve = async () => {
    try {
      const r = await axios.get(`${API}/rate-settings/resolve`, { ...authHeaders, params: { customer_id: resCust || undefined, trade_id: resTrade || undefined } });
      setResolved(r.data);
    } catch (e) { toast.error('Could not resolve'); }
  };

  if (!internal) {
    return <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="rate-settings-locked"><Lock size={16} /> Rate settings are restricted to authorized internal users.</div>;
  }

  return (
    <div data-testid="rate-settings-manager" className="space-y-6">
      <p className="text-sm text-slate-500">Configurable, versioned rates resolved by precedence: <span className="font-semibold text-[#0E2A47]">Customer + Trade → Customer → Trade → TFS Global</span>. Unset fields show "Rate required" and are never treated as zero. Internal only — never shown to clients or field personnel.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Editor */}
        <Card className="border-slate-200 rounded-2xl">
          <CardHeader><CardTitle className="text-base text-[#0E2A47] flex items-center gap-2"><Sliders size={16} /> New / Update Rate Set</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Scope</Label>
                <Select value={scope} onValueChange={(v) => { setScope(v); setCustomerId(''); setTradeId(''); }}>
                  <SelectTrigger data-testid="rate-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SCOPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Effective Date</Label>
                <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} data-testid="rate-effective-date" />
              </div>
              {scope.includes('customer') && (
                <div>
                  <Label>Customer</Label>
                  <Select value={customerId} onValueChange={setCustomerId}>
                    <SelectTrigger data-testid="rate-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {scope.includes('trade') && (
                <div>
                  <Label>Trade</Label>
                  <Select value={tradeId} onValueChange={setTradeId}>
                    <SelectTrigger data-testid="rate-trade-select"><SelectValue placeholder="Select trade" /></SelectTrigger>
                    <SelectContent>{trades.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {fields.map((f) => (
                <div key={f}>
                  <Label className="text-xs">{labels[f] || f}</Label>
                  <Input type="number" step="any" value={rates[f] ?? ''} placeholder="—"
                    onChange={(e) => setRates((r) => ({ ...r, [f]: e.target.value }))} data-testid={`rate-input-${f}`} />
                </div>
              ))}
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g., FY26 rate update" data-testid="rate-note" />
            </div>
            <Button className="bg-[#0E2A47] w-full" onClick={save} data-testid="rate-save-btn">Save Rate Set (new version)</Button>
          </CardContent>
        </Card>

        {/* Resolver + current */}
        <div className="space-y-6">
          <Card className="border-slate-200 rounded-2xl">
            <CardHeader><CardTitle className="text-base text-[#0E2A47]">Resolve Effective Rates</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Select value={resCust} onValueChange={setResCust}>
                  <SelectTrigger data-testid="resolve-customer-select"><SelectValue placeholder="Customer (optional)" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={resTrade} onValueChange={setResTrade}>
                  <SelectTrigger data-testid="resolve-trade-select"><SelectValue placeholder="Trade (optional)" /></SelectTrigger>
                  <SelectContent>{trades.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={doResolve} data-testid="resolve-btn">Resolve</Button>
              {resolved && (
                <div className="text-xs space-y-1" data-testid="resolve-output">
                  {fields.map((f) => (
                    <div key={f} className="flex justify-between border-b border-slate-100 py-1">
                      <span className="text-slate-500">{labels[f] || f}</span>
                      <span className={resolved.resolved[f] === null ? 'text-amber-600 font-semibold' : 'text-[#0E2A47] font-semibold'}>
                        {resolved.resolved[f] === null ? 'Rate required' : `${resolved.resolved[f]} · ${resolved.sources[f]}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 rounded-2xl">
            <CardHeader><CardTitle className="text-base text-[#0E2A47]">Current Rate Sets</CardTitle></CardHeader>
            <CardContent className="p-0 divide-y divide-slate-100" data-testid="rate-current-list">
              {current.map((s) => (
                <div key={s.id} className="px-4 py-2.5 text-sm flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-[#0E2A47]">{SCOPES.find((x) => x.value === s.scope_type)?.label || s.scope_type}</span>
                    {s.customer_name && <span className="text-slate-500"> · {s.customer_name}</span>}
                    {s.trade_name && <span className="text-slate-500"> · {s.trade_name}</span>}
                  </div>
                  <span className="text-xs text-slate-400">v{s.version} · eff {s.effective_date}</span>
                </div>
              ))}
              {current.length === 0 && <div className="py-10 text-center text-slate-400 text-sm">No rate sets configured yet.</div>}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RateSettingsManager;
